/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  FormContext,
  FormSchemaFields,
  UploadControlContextProvider,
} from "@stormwater-software/lib-form";
import { UIControlType } from "@stormwater-software/lib-form-control-types";
import { orderBy } from "lodash";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import AppContext from "../../../contexts/AppContext";
import { AppDivisionContext } from '../../../contexts/AppDivisionContext';
import { ProjectContext } from "../../../contexts/ProjectContext";
import ProjectPermissionContext from "../../../contexts/ProjectPermissionContext";
import RolesContext from "../../../contexts/RolesContext";
import legendItemApi from "../../../utils/api/legendItem";
import { positionableApi } from "../../../utils/api/positionable";
import uploadApi from "../../../utils/api/upload";
import { getCurrentDateTime, parseYMD } from "../../../utils/date";
import { toCalendarDate } from "../../../utils/calendarDate";
import Loading from "../../Shared/ResourceIndex/Loading";
import ObservationsFormControl from "./Observations/ObservationsFormControl";
import { questionConfigValueApi } from "../../../utils/api/questionConfigValue";
import { inspectionQuestionApi } from "../../../utils/api/inspectionQuestion";
import clientApi from "../../../utils/api/client";
import projectApi from "../../../utils/api/project";
import findingTypesApi from "../../../utils/api/findingTypes";
import inspectionApi from "../../../utils/api/inspection";
import findingApi from "../../../utils/api/finding";
import useSaveMarkupOnEdit from "./useSaveMarkupOnEdit";
import { Button, Modal } from "@stormwater-software/lib-ui";
import { toast } from "react-toastify";
import TenantFeatureContext from "../../../contexts/TenantContext";
import EXIF from 'exif-js';
import { dmsToDecimal, getCurrentPosition, showLocationOnMap } from "../../../utils/googlemaps";
import templatePriorityOptionsApi from "../../../utils/api/templatePriorityOptionsApi";
import schedulerApi from '../../../utils/api/inspectionSchedular';
import { projectAssetsApi } from '../../../utils/api/projectAsset';
import { InspectionContext } from "../../../contexts/InspectionContext";

/**
 * SW2-2498: the Action Plan's limit, matching the backend (finding_action_note.notes is
 * VARCHAR(1000)). `validation.maxLength` also sets the textarea's maxLength, so typing and pasting
 * stop at the limit; the counter shows how much is left.
 */
export const ACTION_PLAN_MAX_LENGTH = 1000;

const actionPlanCounter = ({ value }) => (
  <p className='form-text form-text-info' style={{ textAlign: 'right', marginTop: 0 }}>
    {(value || '').length} / {ACTION_PLAN_MAX_LENGTH}
  </p>
);

const findingObservationTemplates = [
  {
    type: 'CA',
    label: 'Corrective Action Notice',
    content:
      'Corrective Action Item is to be addressed next business day after the inspection, per scheduled control measure maintenance.',
  },
  {
    label: 'Completed as recommended',
    content: 'Completed as recommended',
  },
];

/**
 * Templates that get the Legend Item field regardless of the Enhanced Findings flags.
 *
 * Note these are NOT the same strings as the `['CDOT', 'CDOT-1177']` list that gates
 * condition/control_measure a few lines further down in getSchema — that is a different feature
 * with deliberately different template names. Do not consolidate the two.
 */
const LEGEND_ITEM_TEMPLATE_NAMES = [
  'Delaware',
  'CDOT 1176 06/2024',
  'CDOT 1177 - 5/2024',
];

/**
 * Whether the Legend Item select should be shown at all.
 *
 * Previously duplicated verbatim between getSchema and the legend-items fetch effect; they must
 * agree, or the form either renders a field it never populates or fetches options it never shows.
 */
export const isLegendItemFieldEnabled = (templateName, findingsMetricsTracking) =>
  LEGEND_ITEM_TEMPLATE_NAMES.includes(templateName) || !!findingsMetricsTracking;

const ApprovalStatus = ({ status, name }) => {
  const getStatusStyle = (status) => {
    switch (status) {
      case 'Pending':
        return { color: '#FF9F43', fontWeight: 'bold' };
      case 'Approved':
      case 'Not Required':
      case 'ApprovedNoName':
        return { color: '#48af98', fontWeight: 'bold' };
      default:
        return {};
    }
  };

  const getStatusText = (status, name) => {
    switch (status) {
      case 'Pending':
        return 'Pending Approval';
      case 'Approved':
        return `Approved By ${name}`;
      case 'ApprovedNoName':
        return `Approved`;
      case 'Not Required':
        return 'Not Required';
      default:
        return '';
    }
  };

  return (
    <span style={getStatusStyle(status)}>
      {getStatusText(status, name)}
    </span>
  );
};

const updateSignature = async (formData, formContext) => {
  await findingApi.addSignature(formData.actionId)
    .then(async () => {
      toast("Signature Updated")
      formContext.set("isSigned", true)
    })
}

const deleteNotes = async (formData, formContext) => {
  await findingApi.deleteNotes(formContext.value.id, formData.actionId)
    .then(async () => {
      toast("Action Plan removed")
      formContext.set("actionPlans", false)
      formContext.set("signeeName", null)
      formContext.set("actionObservation", null)
      formContext.set("actionId", null)
    })

}

const removeSignature = async (formData, formContext) => {
  await findingApi.removeSignature(formData.actionId)
    .then(async () => {
      toast("Signature Removed")
      formContext.set("isSigned", false)
    })
}

export const defaultCompletionTemplateContent =
  findingObservationTemplates[1].content;

export const getFindingCommentsSchema = (
  canCreate = false,
  canRead = false,
  canEdit = false,
  canDelete = false,
  readOnly = false,
  showObservationDatesOfFindings
) => {
  const schema = {
    /** field to add comments */
    comments: {
      label: 'Observations',
      controlType: UIControlType.textareaTmpl,
      'aria-label': 'Observations',
      templates: findingObservationTemplates,
      disabled: !canCreate,
      style: { marginBottom: '0.25rem' },
    },
  };

  if (canRead) {
    /** display-only "field" for previous comments */
    schema.observations = {
      controlType: UIControlType.custom,
      renderControl: (controlProps) => (
        <ObservationsFormControl
          {...controlProps}
          canEdit={canEdit}
          canDelete={canDelete}
          readOnly={readOnly}
          showObservationDatesOfFindings={showObservationDatesOfFindings}
        />
      ),
    };
  }

  return schema;
};

export const getActionModalSchema = (
  formData,
  displayStyle,
  template,
  permCheck,
  isDownloaded=false
) => {
  if (template.findings_action) {
    const schema = {
      actionPlans: {
        label: 'Add Interim Action Plan',
        controlType: UIControlType.checkbox,
        style: displayStyle,
        disabled: isDownloaded,
      }
    };

    if (formData.actionPlans) {
      schema.actionObservation = {
        label: 'Action Plan',
        controlType: UIControlType.textarea,
        style: { marginBottom: '0.25rem' },
        disabled: !permCheck('create', 'Action Plan') || isDownloaded,
        validation: { maxLength: ACTION_PLAN_MAX_LENGTH },
        renderAfter: actionPlanCounter,
      }
    }

    return schema;
  }

  return null
};

export const getActionFullScreenSchema = (
  formData, template, formContext, displayStyle, permCheck, isDownloaded = false
) => {
  const status = () => {
    if (formData.isSigned) {
      if (formData.signeeName) {
        return (
          <div>
            <ApprovalStatus status="Approved" name={formData.signeeName} />
          </div>
        )
      }

      return (
        <div>
          <ApprovalStatus status="ApprovedNoName" />
        </div>
      )
    }

    return (
      <div>
        <ApprovalStatus status="Pending" />
      </div>
    )
  }

  if (formData.actionPlans) {
    const schema = {
      actionObservation: {
        label: 'Action Plan',
        controlType: UIControlType.textarea,
        style: { marginBottom: '0.25rem', width: '100%' },
        disabled: formData.isSigned || !permCheck('create', 'Action Plan') || isDownloaded,
        validation: { maxLength: ACTION_PLAN_MAX_LENGTH },
        renderAfter: actionPlanCounter,
      }
    };

    if (formData.actionId) {
      schema.actionFooter = {
        controlType: UIControlType.custom,
        renderControl: (controlProps) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
            {status()}
            {
              !formData.isSigned &&
              <div>
                <Button outline={true} small={true} disabled={!permCheck("update", "Action Plan")} onClick={() => updateSignature(formData, formContext)} style={{ marginRight: '10px' }}>
                  Approval
                </Button>
                {permCheck('create', 'Action Plan') && (
                  <Button
                    outline={true}
                    className="col-danger"
                    style={{ borderColor: '#FF4C51' }}
                    small={true}
                    onClick={() => deleteNotes(formData, formContext)}
                    disabled={isDownloaded}
                  >
                    Remove Action Plan
                  </Button>
                )}
              </div>
            }
            {
              formData.isSigned && !formData.date_completed && permCheck("update", "Action Plan") &&
                <div>
                <Button outline={true} small={true} onClick={() => removeSignature(formData, formContext)} style={{ marginRight: '10px' }} disabled={isDownloaded}>
                    Clear Approval
                  </Button>
                </div>
            }
          </div>
        )
      }
    }

    return schema;
  }
  if (template.findings_action && permCheck('create', 'Action Plan')) {
    const schema = {
      actionPlans: {
        label: 'Add Interim Action Plan',
        controlType: UIControlType.checkbox,
        style: displayStyle,
        disabled: isDownloaded,
      }
    };

    return schema;
  }

  return null
};

export const extractFindingData = (model, inspectionId) => {
  const {
    id,
    commentables,
    date_completed,
    date_initiated,
    finding_uploads = [],
    location,
    type,
    condition,
    control_measure,
    legend_item_id,
    legend_positionable_id,
    legend_positionable_label,
    priority,
    completed_by,
    initiated_by,
    is_completed_by_guest,
    project_asset_id,
  } = model;

  const images = finding_uploads
    .filter((findingUpload) => !findingUpload.is_completed)
    .map(toImageUpload);
  const completedImages = finding_uploads
    .filter((findingUpload) => findingUpload.is_completed)
    .map(toImageUpload);

  let modified_commentables = commentables.map(note => ({
    ...note,
    ...(note.author.first_name === "Guest" && note.author.last_name === "User" 
        ? { completed_by, is_completed_by_guest } 
        : {})
  }));

  return {
    id,
    inspectionId,
    images: images,
    completedImages: completedImages,
    observations: orderBy(modified_commentables || [], 'created_at', 'desc'),
    comments: '',
    type: type ? type : 'CA',
    location: location ? location : 'See site inspection map for location',
    date_initiated: date_initiated
      ? parseYMD(date_initiated.substr(0, 10))
      : null,
    date_completed: date_completed
      ? parseYMD(date_completed.substr(0, 10))
      : null,
    condition,
    control_measure,
    legend_item_id: legend_item_id,
    actionObservation: model.action_note ? model.action_note.notes : null,
    actionPlans: model.finding_action,
    actionId: model.action_note_id,
    isSigned: model.action_note ? model.action_note.is_signed : false,
    signeeName: model.action_note && model.action_note.commentableSignature ? model.action_note.commentableSignature.signee_name : null,
    priority: priority,
    completed_by,
    initiated_by,
    is_completed_by_guest,
    project_asset_id: project_asset_id || null,
    // SW2-2230. The label is read-only context for the synthetic picker option; the server
    // recomputes it and ignores it on write.
    legend_positionable_id: legend_positionable_id ?? null,
    legend_positionable_label: legend_positionable_label ?? null,
  };
};

export const toImageUpload = ({ id, upload, annotations }) => {
  return {
    id,
    guid: upload.GUID,
    gps_latitude: upload.gps_latitude,
    gps_longitude: upload.gps_longitude,
    timestamp: upload.timestamp,
    type: upload.mime_type,
    name: upload.name,
    rotation: upload.rotation ? upload.rotation : 0,
    annotations: annotations ? annotations : [],
  };
};

export const getInitialData = (defaultDateInitiated, type) => ({
  type: type,
  location: "See site inspection map for location",
  date_initiated: defaultDateInitiated,
  date_completed: null,
  images: [],
  observations: [],
  comments: '',
});


/**
 *
 * @param {boolean} commentOnly
 * @param {string} userRole User's Role.Name
 * @param {function} permCheck
 * @param {any} template
 * @param {boolean} readOnly disables inputs
 */
function getSchema(
  templateID,
  commentOnly,
  userRole,
  permCheck,
  template,
  readOnly,
  disableFieldRelatedToDateCompleted,
  formData,
  enableEditComments,
  legendItems,
  archived,
  projectStartDate,
  conditionOptions,
  isPriorityEnabled,
  priorityOptions,
  disableImage,
  findingsMetricsTracking,
  inspectionId,
  modal,
  formContext,
  isDownloaded=false,
  getLocation = () => { },
  isGuest,
  isCompleted,
  fromFindingAdd,
  isFindingBackdatingDisabled,
  showObservationDatesOfFindings,
  requireClosurePhotos = false,
  assetTrackingEnabled = false,
  assetOptions = [],
  fixedAssetId = null,
  legendPlacementEnabled = false,
  legendPlacementOptions = [],
) {

  const templateConditionOptions =
    conditionOptions && conditionOptions.length
    ? conditionOptions
    : template.condition_options;

  const displayStyle = { display: commentOnly ? 'none' : null };

  /**
   * SW2-2230 — Legend Item and Legend Item ID share a row of their own, below the dates, but only
   * when the ID field is actually shown. Otherwise Legend Item keeps the default full width.
   *
   * The bases are deliberately lopsided. This is a plain `flex-flow: row wrap` container with no
   * row concept, so items pack greedily: the date pair uses 25% each, leaving 50% free, and
   * anything that fits in that gap gets pulled up alongside them. A 55% basis cannot fit, so
   * Legend Item is forced onto the next line and takes the ID field with it. The two then sum to
   * 90%, leaving room for gutters, and both grow to fill — landing near 60/40, which suits the
   * content anyway since names are long and ids are one or two characters.
   *
   * `minWidth` is what makes them stack on small screens: a percentage basis scales with the
   * container, so without it the pair would stay side-by-side at every width and merely shrink.
   */
  const showLegendPlacement =
    legendPlacementEnabled && legendPlacementOptions.length > 0;
  const legendItemStyle = { flex: '1 1 55%', minWidth: '14rem', ...displayStyle };
  const legendIdStyle = { flex: '1 1 35%', minWidth: '10rem', ...displayStyle };
  const [findingTypes, setFindingTypes] = useState([]);
  const handleName = (name) => {

    if (name === "A") {
      return "Achievement"
    }
    else if (name === "MI") {
      return "Maintenance Item"
    }
    else if (name === "CA") {
      return "Corrective Action"
    }
    else {
      return name
    }
  }

  const canUpdate = !permCheck("update", "Findings")

  const restrictedForRoles =
    ["BMP Contractor", "Client User", "Permittee Plus", "Permittee Basic"].indexOf(userRole) > -1;

  React.useEffect(() => {
    (async () => {
      if (!templateID) {
        const { inspection } = await inspectionApi.get(inspectionId)

        templateID = inspection.template_id
      }
      const data = await findingTypesApi.index(templateID)
      const findingName = data.filter((ele) => ele.details.is_enabled)
        .map((ele) => ({ value: ele.name, label: handleName(ele.name) }))

      setFindingTypes(findingName)
    })();
  }, []);

  const schema = {
    type: {
      label: "Finding Type",
      controlType: UIControlType.select,
      validation: { required: true },
      options: findingTypes,
      style: displayStyle,
      disabled: readOnly || canUpdate || restrictedForRoles || isDownloaded,
      className: "finding-mobile",
    },
    location: {
      label: 'Location',
      // From an asset the location is pre-filled with the asset's lat/long,
      // so the capture-location button is hidden for that flow.
      controlType: fixedAssetId ? UIControlType.text : UIControlType.textWithBtn,
      ...(fixedAssetId
        ? {}
        : { btnLabel: 'Capture Location', handleBtnClick: getLocation }),
      validation: {
        required: true,
      },
      style: displayStyle,
      disabled: readOnly || canUpdate || restrictedForRoles,
    },
    date_initiated: {
      label: 'Date Initiated',
      controlType: UIControlType.date,
      validation: {
        required: true,
        minDate: isFindingBackdatingDisabled
          ? new Date()
          // SW2-2244: `moment(projectStartDate).toDate()` kept the stored
          // midnight-UTC instant, so west of UTC the bound sat a day before the
          // project actually started and a finding could be dated too early.
          : toCalendarDate(projectStartDate) ?? undefined,
        maxDate: new Date(),
      },
      style: { flex: "1 1 25%", ...displayStyle },
      disabled: readOnly || canUpdate || restrictedForRoles || isDownloaded,
    },
    initiated_by: {
      label: 'Initiated By',
      controlType: UIControlType.text,
      style: { flex: '1 1 25%', ...displayStyle },
      disabled: true,
    },
    date_completed: {
      label: 'Date Completed',
      controlType: UIControlType.date,
      style: { flex: '1 1 25%', ...displayStyle },
      validation: {
        maxDate: new Date(),
        minDate: isFindingBackdatingDisabled ? new Date() : formData.date_initiated ? formData.date_initiated : undefined,
      },
      disabled: formData.actionPlans
        ? (formData.isSigned ? disableFieldRelatedToDateCompleted || canUpdate : true)
        : (disableFieldRelatedToDateCompleted || canUpdate),
    },
    completed_by: {
      label: 'Completed By',
      controlType: UIControlType.text,
      style: { flex: '1 1 25%', ...displayStyle },
      disabled: !isGuest || (formData.actionPlans
        ? (formData.isSigned ? disableFieldRelatedToDateCompleted || canUpdate : true)
        : (disableFieldRelatedToDateCompleted || canUpdate)),
    },
    legend_item_id: {
      label: 'Legend Item',
      controlType: UIControlType.select,
      options:
        legendItems.length > 0
          ? legendItems.map((legItem) => {
              return {
                label: legItem.name,
                value: legItem.id,
              };
            })
          : [],
      // Full width on its own; drops to half only when Legend Item ID appears beside it.
      // This has to be conditional rather than a permanent flex basis: `flex-grow` only fills space
      // left over once items are placed on a row, so a standing 45% basis let the Date Initiated /
      // Date Completed pair slide up alongside instead of staying on their own row.
      style: showLegendPlacement ? legendItemStyle : displayStyle,
      disabled: readOnly,
    },
    /**
     * SW2-2230. A *specific* legend item placed on one of this inspection's site maps
     * ("Silt Fence 3"), as opposed to the legend item type above ("Silt Fence").
     *
     * Omitted entirely when there is nothing to pick — the user falls back to the type field.
     * The exception is a finding that already has a placement: that must stay visible (and
     * clearable) even when the placement is no longer offered, which is what the synthetic option
     * in `legendPlacementOptions` is for.
     */
    ...(showLegendPlacement
      ? {
          legend_positionable_id: {
            label: 'Legend Item ID',
            controlType: UIControlType.select,
            options: legendPlacementOptions,
            style: legendIdStyle,
            disabled: readOnly,
          },
        }
      : {}),
    ...(assetTrackingEnabled || fixedAssetId
      ? {
          project_asset_id: {
            label: 'Asset',
            controlType: UIControlType.select,
            options: assetOptions,
            disabled: readOnly || isDownloaded || !!fixedAssetId,
          },
        }
      : {}),
    ...getFindingCommentsSchema(
      archived || disableFieldRelatedToDateCompleted ? false : permCheck("create", "finding_comments"),
      permCheck("read", "finding_comments"),
      archived
        ? false
        : enableEditComments
          ? permCheck("update", "finding_comments")
        : false,
      archived
        ? false
        : enableEditComments
          ? permCheck("delete", "finding_comments")
        : false,
      readOnly,
      showObservationDatesOfFindings
    ),

    control_measure: {
      label: "Control Measure",
      style: { flex: "1 1 50%", ...displayStyle },
      disabled: readOnly || canUpdate || isDownloaded,
      validation: { required: true },
    },
    condition: {
      label: 'Condition',
      controlType: UIControlType.select,
      options: templateConditionOptions.map(option => ({
        label: option,
        value: option,
      })),
      style: { flex: "1 1 50%", ...displayStyle },
      disabled: readOnly || canUpdate || isDownloaded,
      validation: { required: true },
    },
    priority: {
      label: 'Priority',
      controlType: UIControlType.select,
      options: priorityOptions.map(option => ({
        label: option.label,
        value: option.label,
      })),
      style: { ...displayStyle },
      disabled: readOnly || canUpdate || isDownloaded,
      validation: { required: true },
    },
    images: {
      label:
        (formData.completedImages && formData.completedImages.length) || (requireClosurePhotos && formData.date_completed)
          ? 'Initial Images'
          : '',
      controlType: UIControlType.imageUpload,
      style: displayStyle,
      disabled: readOnly || (requireClosurePhotos && !!formData.date_completed),
    },
    ...(modal
      ? getActionModalSchema(formData, displayStyle, template, permCheck,isDownloaded)
      : getActionFullScreenSchema(formData, template, formContext, displayStyle, permCheck,isDownloaded)
    ),
  };

  if (requireClosurePhotos && formData.date_completed) {
    schema.completedImages = {
      label: 'Closure Photos (required)',
      controlType: UIControlType.imageUpload,
      style: displayStyle,
    };
  } else if (formData.completedImages && formData.completedImages.length) {
    schema.completedImages = {
      label: 'Completion Images',
      controlType: UIControlType.imageUpload,
      style: displayStyle,
    };
  }

  if(fromFindingAdd){
    delete schema.initiated_by;

    // SW2-2136: a new finding cannot be closed in the same save when its form requires closure
    // photos — photos are uploaded after the finding exists, and the server refuses it. The
    // user saves the finding, then closes it with a closure photo. `null` is "not known yet",
    // and is hidden too: see useRequireClosurePhotos.
    if (requireClosurePhotos !== false) {
      delete schema.date_completed;
      delete schema.completedImages;
    }
  }

  if(!isGuest && !isCompleted){
    delete schema.completed_by;
  }

  if (!isLegendItemFieldEnabled(template.name, findingsMetricsTracking)) {
    delete schema.legend_item_id;
  }

  /** @todo refactor to use schema from server */

  if (
    !(["CDOT", "CDOT-1177"].includes(template.name) || (conditionOptions && conditionOptions.length))
  ) {
    delete schema.condition;
    delete schema.control_measure;
  }

  /**
   * SW2-2242 — deliberately NOT hiding `control_measure` when Enhanced Findings is on.
   *
   * An earlier revision deleted it here, on the reasoning that the Legend Item select supersedes
   * the free-text field. That is true for the *new* CDOT 1176 06/2024 and CDOT 1177 - 5/2024 forms
   * — but those never render `control_measure` in the first place (their names are not in the list
   * above and they carry no Condition Group, so `conditionOptions` is empty), and 0 of their 1,904
   * findings have ever stored a value in it.
   *
   * The forms it actually affected were the legacy `CDOT` and `CDOT-1177` templates, where the
   * column is the primary record: 3,871 of 4,125 and 89 of 92 findings respectively carry
   * free-text control measures, and CDotLandscape/CDotForm/CDot1177FormLandscape print
   * `finding.controlMeasure` directly. Hiding the input there blanked the printed column for every
   * new finding, which is what surfaced on inspection 409742 (template 155, legacy `CDOT`).
   *
   * So legacy keeps its free-text entry untouched and the legend item picker stays additive. The
   * double-entry that leaves on legacy CDOT is pre-existing behaviour, and the deprecation notice
   * for those forms is the intended answer to it rather than a gate change here.
   */

  if (
    !isGuest &&
    (!permCheck('read', 'Finding Images') ||
      disableImage ||
      !permCheck('all', 'Download'))
  ) {
    delete schema.images;
    delete schema.completedImages;
  }

  if (!isPriorityEnabled) {
    delete schema.priority;
  }

  return schema;
}

/**
 * UI to create a note. Does not save data
 */

function FindingsForm({
  // SW2-2572: told after a photo's markup is saved on its own, with the saved rows, so a caller
  // holding its own copy of the finding (the map editor) can update it.
  onMarkupSaved,
  commentOnly,
  templateName,
  templateID,
  readOnly,
  disableFieldRelatedToDateCompleted,
  defaultObservation,
  enableEditComments,
  inspectionId,
  modal,
  setIsEnabledPhotoMetadata = () => { },
  noMetadataImage = [],
  showMetadataWarning = false,
  setShowMetadataWarning = () => { },
  isGuest,
  isCompleted,
  fromFindingAdd,
  requireClosurePhotos = false,
  fixedAssetId = null,
}) {
  const appStore = useContext(AppContext);
  const appDivisionContext = useContext(AppDivisionContext);
  const user = appStore.get("user");
  const permCheck = useContext(RolesContext).userHasPermission;
  const projectPermissionContext = useContext(ProjectPermissionContext);
  const projectContext = useContext(ProjectContext);
  const tenantFeatureContext = useContext(TenantFeatureContext);
  const inspectionContext = useContext(InspectionContext);
  const [loading, setLoading] = useState(false);
  // SW2-2136: the photo being renamed from the gallery, and the name being typed.
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [legendItems, setLegendItems] = useState([]);
  const formContext = useContext(FormContext);
  // the annotation editor's Save stores a saved photo's markup straight away
  useSaveMarkupOnEdit({
    enabled: !readOnly && !fromFindingAdd && permCheck('all', 'Annotations'),
    onSaved: onMarkupSaved,
  });
  const [questionId, setQuestionId] = useState(null);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [clientDisableImages, setClientDisableImages] = useState(false);
  const [showObservationDatesOfFindings, setShowObservationDatesOfFindings] = useState(false);
  const [findingsMetricsTracking, setFindingsMetricsTracking] = useState(false);
  const [enablePhotoMetadataClient, setEnablePhotoMetadataClient] = useState(false);
  const [isPriorityEnabled, setIsPriorityEnabled] = useState(false);
  const [findingAction, setFindingAction] = useState(false);
  /**
   * SW2-2242 — the template this *inspection* actually runs on.
   *
   * The `templateName` prop is sourced from `ProjectContext.template`, which has been permanently
   * `null` since migration 20251001101113 removed `projects.inspection_template_id` (assignment
   * moved to the `project_inspection_templates` junction). ProjectContext.tsx:389 still guards on
   * that dead column, so it never calls setTemplate — and every template-name gate below silently
   * failed, taking the Legend Item, Condition and Control Measure fields with it.
   *
   * The fetch on the next effect already returns the authoritative row: it resolves through
   * `inspections.template_id`, which is the same column the PDF pipeline reads
   * (inspectionReportDirector -> findTemplateByInspection). Gating on it is what makes the form and
   * the printed report agree by construction rather than by coincidence.
   */
  const [inspectionTemplate, setInspectionTemplate] = useState(null);
  const [project, setProject] = useState((projectContext || {}).project);
  const [assetTrackingEnabled, setAssetTrackingEnabled] = useState(false);
  const [assetOptions, setAssetOptions] = useState([]);
  /** `lat, long` per asset id, for the Location pre-fill. */
  const assetLocations = useRef({});
  const assetPrefillReady = useRef(false);
  const hasAssetTrackingFeature =
    tenantFeatureContext.tenantHasFeature('Asset Tracking');
  /** SW2-2230 — placed legend items ("Silt Fence 3") for this inspection's site maps. */
  const [legendPlacements, setLegendPlacements] = useState([]);
  const legendPlacementsById = useRef({});
  const legendPlacementPrefillReady = useRef(false);
  /**
   * Whether the placement fetch has finished, either way.
   *
   * Distinct from `legendPlacements.length`: an empty list is a legitimate answer (no placements on
   * this inspection's maps) *and* what we hold before the request returns, and the two demand
   * opposite handling for a map prefill — one means "this id is not valid here, drop it", the other
   * means "not yet, wait".
   */
  const [legendPlacementsSettled, setLegendPlacementsSettled] = useState(false);
  // Strict subset of the Legend Item gate, so the placement field can never render without its
  // parent field: findingsMetricsTracking already folds in the tenant/client/project Enhanced
  // Findings booleans.
  const legendPlacementEnabled =
    tenantFeatureContext.tenantHasFeature(
      'Enable Enhanced Findings Map Item Selection',
    ) && !!findingsMetricsTracking;
  /**
   * SW2-2242 — the name every template gate below keys on.
   *
   * Prefers the inspection's own template; falls back to the prop while the fetch is in flight (and
   * for the offline shape that carries no template). The prop is also the only source SourceModelForm
   * can supply before its own fix, so keeping it as a fallback avoids a hard dependency between the
   * two files.
   */
  const gateTemplateName = inspectionTemplate?.name ?? templateName;
  const {
    observations,
    commentables,
    date_initiated: dateInitiated,
    date_completed: dateCompleted,
    actionPlans,
    actionObservation,
    images,
    completedImages,
    location,
  } = formContext.value;

  // Derived (not latched) so photo visibility recovers once the async
  // tenant-feature query resolves. Previously this was stored in state and set
  // inside a [project] effect, so a mount before features loaded stuck it at
  // `true` and hid photos on the details page. FindingCard evaluates the same
  // conditions inline during render, which is why list thumbnails were unaffected.
  const disableImage =
    user.roleName.startsWith('Public') ||
    clientDisableImages ||
    !tenantFeatureContext.tenantHasFeature('Enable Photo Uploads');

  useEffect(() => {
    (async () => {
      if (project) {
        if (project.client_id) {
          const c = await clientApi.getCustomFields(project.client_id, ['finding_metric_tracking', 'disable_images', 'enable_photo_metadata_requirement', 'show_observation_dates_of_findings']);

          if (c && c.finding_metric_tracking) {
            setFindingsMetricsTracking(c.finding_metric_tracking && project.finding_metric_tracking && tenantFeatureContext.tenantHasFeature('Enable Enhanced Findings'));
          }
          if (c && c.disable_images) setClientDisableImages(c.disable_images);
          if (c && c.show_observation_dates_of_findings) setShowObservationDatesOfFindings(c.show_observation_dates_of_findings);
          if (c && c.enable_photo_metadata_requirement) {setEnablePhotoMetadataClient(c.enable_photo_metadata_requirement);
            setIsEnabledPhotoMetadata(c.enable_photo_metadata_requirement && tenantFeatureContext.tenantHasFeature('Enable Photo Metadata Requirement') && project.enable_photo_metadata_requirement);
          }
        }
      } else {
        const url = window.location.href;
        const regex = /projects\/(\d+)\/map/;
        const m = regex.exec(url);

        if (m !== null && m.length > 1) {
          const projectId = m[1];
          const projectDetail = await projectApi.get(projectId)

          setProject(projectDetail);
        }
      }
    })();
  }, [project]);

  useEffect(() => {
    (async () => {
      if (inspectionId) {
        const res = await inspectionQuestionApi.show(inspectionId);

        // SW2-2242. Optional chaining because the offline fallback can return a cached shape
        // without a template; the gate then falls back to the `templateName` prop.
        setInspectionTemplate(res?.template ?? null);
        setFindingAction(res.template.findings_action);
        setIsPriorityEnabled(res.template.is_priority_dropdown_enabled);
        if (res && res.template && res.template.question_groups) {
          outer: for (const group of res.template.question_groups) {
            if (group.name === "Condition Group") {
              for (const question of group.questions) {
                if (question.input_label === "Condition") {
                  setQuestionId(question.id);
                  break outer;
                }
              }
            }
          }
        }
      }
    })();
    // SW2-2242. Was `[]`, which pinned the fetch to whichever inspectionId happened to be set on
    // first mount. FindingDetail and the map editor both remount this form against a different
    // inspection without unmounting it, so a stale template could survive the switch.
  }, [inspectionId]);

  useEffect(() => {
    (async () => {
      if (questionId) {
        const questionConfigValues = await questionConfigValueApi.index(
          questionId,
        );

        if (questionConfigValues) {
          for (const questionConfigValue of questionConfigValues) {
            if (
              "questionTypeFieldId" in questionConfigValue &&
              typeof questionConfigValue.value === "string"
            ) {
              setConditionOptions(JSON.parse(questionConfigValue.value));
              break;
            }
          }
        }
      }
    })();
  }, [questionId]);

  useEffect(() => {
    (async () => {
      if (!templateID) {
        const { inspection } = await inspectionApi.get(inspectionId)

        templateID = inspection.template_id
      }
      templatePriorityOptionsApi
        .getByTemplateId(templateID)
        .then((data) => {setPriorityOptions(data)})
        .catch(() => toast.error("Failed to load finding priority options"));
      })();
  }, [templateID]);

  useEffect(() => {
    if (isLegendItemFieldEnabled(gateTemplateName, findingsMetricsTracking)) {
      project &&
        legendItemApi
          .indexByProject(project.id, appDivisionContext.appDivisionId)
          .then((data) => {
            setLegendItems(data);
          })
          .catch((err) => {
            // Without this the form spins forever on any failure — including the
            // offline fallback rethrowing for a project that was never
            // downloaded — plus an unhandled rejection.
            console.error('Error fetching legend items:', err);
          })
          .finally(() => {
            setLoading(false);
          });
    }
    else {
      setLoading(false);
    }
    // SW2-2242. `gateTemplateName` added: it arrives asynchronously now, so without it this effect
    // would decide off the initial (usually undefined) value and never fetch the options for a
    // field getSchema goes on to render.
  }, [project, findingsMetricsTracking, gateTemplateName]);

  /**
   * SW2-2230 — fetch the placed legend items for this inspection.
   *
   * Deliberately a separate effect from the legend-items fetch above, and it must never touch
   * `setLoading`: that effect gates first paint of the entire finding form, and this picker is
   * optional by design. A failure here should cost the user the picker, not the form.
   */
  useEffect(() => {
    if (isGuest || !legendPlacementEnabled || !inspectionId) {
      setLegendPlacements([]);
      legendPlacementsById.current = {};

      // Deliberately NOT marked settled.
      //
      // `legendPlacementEnabled` folds in `findingsMetricsTracking`, which starts false and is
      // populated by an async client lookup — so "off" here means "off, or we have not found out
      // yet", and the two are indistinguishable at this point. Treating it as a settled answer let
      // the normalise effect below wipe the map's right-click prefill on first render, before the
      // real fetch had even run; by the time the gate flag arrived the value was gone for good.
      //
      // Only a fetch that actually completed can settle this. When the feature is genuinely off
      // the prefill just stays in the form, which costs nothing: the picker never renders, and
      // `resolveHeadIdForWrite` drops the column server-side for an unflagged tenant anyway.
      return;
    }

    setLegendPlacementsSettled(false);

    let cancelled = false;

    positionableApi
      .legendPlacementsByInspection(inspectionId)
      .then((data) => {
        if (cancelled) {
          return;
        }

        const placements = data || [];

        legendPlacementsById.current = placements.reduce((acc, placement) => {
          acc[placement.id] = placement;

          return acc;
        }, {});
        setLegendPlacements(placements);
        setLegendPlacementsSettled(true);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }

        // Degrade to the legend-item-name field rather than surfacing an error the user cannot act
        // on. Logged so a 500 is still diagnosable.
        console.error('Error fetching placed legend items:', err);
        legendPlacementsById.current = {};
        setLegendPlacements([]);
        setLegendPlacementsSettled(true);
      });

    return () => {
      cancelled = true;
    };
  }, [inspectionId, legendPlacementEnabled, isGuest]);

  /**
   * SW2-2230 — normalise the select's value.
   *
   * The select emits `e.target.value` verbatim, so this receives `'123'` and `''`. Offline those
   * strings go straight into Dexie and are replayed on sync, so normalising server-side alone is
   * not enough.
   */
  useEffect(() => {
    const raw = formContext.value.legend_positionable_id;

    if (raw === '') {
      formContext.set('legend_positionable_id', null);

      return;
    }

    if (raw === null || raw === undefined) {
      return;
    }

    const id = Number(raw);

    if (id !== raw) {
      formContext.set('legend_positionable_id', id);
    }
  }, [formContext.value.legend_positionable_id]);

  /**
   * SW2-2230 — the placement is scoped to the chosen Legend Item, so clear it when the user
   * switches to a different type. Without this the finding would keep a placement belonging to the
   * previous legend item while the field silently re-renders with the new type's numbers.
   *
   * The ref guard skips the first run so opening an existing finding does not wipe its saved
   * placement before `legendPlacements` has loaded.
   */
  useEffect(() => {
    if (!legendPlacementPrefillReady.current) {
      legendPlacementPrefillReady.current = true;

      return;
    }

    const assigned = formContext.value.legend_positionable_id;

    if (!assigned) {
      return;
    }

    const assignedId = Number(assigned);
    const placement = legendPlacementsById.current[assignedId];

    // The map's right-click "Add Finding" seeds the *live* positionables row, because that is all a
    // map feature knows. Swap it for that placement's chain head, which is what the options carry
    // and what the finding stores. No-op for an unedited placement, where the two are the same id.
    if (!placement) {
      const byLiveRow = legendPlacements.find(
        (candidate) => Number(candidate.live_positionable_id) === assignedId,
      );

      if (byLiveRow) {
        formContext.set('legend_positionable_id', byLiveRow.id);

        return;
      }
    }

    /**
     * An id we cannot account for, on a finding being created, can only have come from the map
     * prefill — and a prefill is a *live row id* that has never been checked against anything.
     *
     * Previously this returned early whenever the list was empty, so if the placements request
     * failed (offline cache miss, blocked, 500) the raw live id stayed in the form and was
     * submitted, on a form where the picker had never even rendered. The user was made to own a
     * value they were never shown. Drop it.
     *
     * Only for `fromFindingAdd`. On an existing finding the stored value is already a resolved
     * chain head, and failing to find it here just means the placement is no longer on the map —
     * which is exactly what the synthetic "Assigned map placement" option exists to display.
     */
    if (!placement && legendPlacementsSettled && fromFindingAdd) {
      formContext.set('legend_positionable_id', null);

      return;
    }

    const legendItemId = Number(formContext.value.legend_item_id) || null;

    if (placement && legendItemId && Number(placement.legend_item_id) !== legendItemId) {
      formContext.set('legend_positionable_id', null);
    }
  }, [
    formContext.value.legend_item_id,
    legendPlacements,
    legendPlacementsSettled,
    fromFindingAdd,
  ]);

  useEffect(() => {
    if (
      defaultObservation &&
      observations &&
      !observations.length &&
      !commentables
    ) {
      formContext.set('comments', defaultObservation);
    }
  }, [defaultObservation]);

  // SW2-2136: the field is hidden on Add Finding when closure photos are required; clear a date
  // entered before the setting loaded, so it is not sent unseen — and the "Completed as
  // recommended" observation that date filled in below, if it is still untouched.
  useEffect(() => {
    if (fromFindingAdd && requireClosurePhotos !== false && formContext.value.date_completed) {
      formContext.set('date_completed', null);

      if (formContext.value.comments === findingObservationTemplates[1].content) {
        formContext.set('comments', '');
      }
    }
  }, [fromFindingAdd, requireClosurePhotos]);

  //populate Observations with "Completed as recommended" when Date Completed is filled
  useEffect(() => {
    if (formContext.value.date_completed && !formContext.value.comments)
      formContext.set("comments", findingObservationTemplates[1].content)

  }, [formContext.value.date_completed]);

  useEffect(() => {
    if (formContext.value.actionPlans) {
      formContext.set("actionPlans", actionPlans)
      formContext.set("actionObservation", actionObservation)
    } else {
      formContext.set("actionPlans", actionPlans)
      formContext.set("actionObservation", '')
    }

  }, [formContext.value.actionPlans]);

  useEffect(() => {
    if (dateInitiated && dateCompleted && dateInitiated > dateCompleted) {
      formContext.set('date_completed', null);
    }
  }, [dateInitiated, dateCompleted]);

  useEffect(() => {
    const updatedImages = [...(images || [])];

    updatedImages.forEach((image, index) => {
      if (image.file && image.gpsData === undefined) {
        EXIF.getData(image.file, function () {
          const gpsLat = EXIF.getTag(this, 'GPSLatitude');
          const gpsLon = EXIF.getTag(this, 'GPSLongitude');
          const gpsLatRef = EXIF.getTag(this, 'GPSLatitudeRef');
          const gpsLonRef = EXIF.getTag(this, 'GPSLongitudeRef');
          const timestamp = EXIF.getTag(this, 'DateTime');
          const capturedInLessThan2Minutes =
            image.file.lastModified &&
            Date.now() - image.file.lastModified < 2 * 60 * 1000;

          const updateImage = (newData) => {
            updatedImages[index] = {
              ...updatedImages[index],
              ...newData,
            };
            formContext.set('images', [...updatedImages]);
          };

          if (gpsLat && gpsLon) {
            const lat = dmsToDecimal(gpsLat, gpsLatRef);
            const lon = dmsToDecimal(gpsLon, gpsLonRef);
            updateImage({
              gpsData: { lat, lon },
              gps_latitude: lat,
              gps_longitude: lon,
              timestamp: timestamp || null,
            });
          } else if (!gpsLat && !gpsLon && capturedInLessThan2Minutes) {
            getCurrentPosition().then(({ latitude, longitude }) => {
              updateImage({
                gpsData: { lat: latitude, lon: longitude },
                gps_latitude: latitude,
                gps_longitude: longitude,
                timestamp: getCurrentDateTime(),
              });
            }).catch((err) => {
              updateImage({
                gpsData: null,
                gps_latitude: null,
                gps_longitude: null,
                timestamp: null,
              })
            })
          } else {
            updateImage({
              gpsData: null,
              gps_latitude: null,
              gps_longitude: null,
              timestamp: null,
            });
          }
        });
      }
    });
  }, [images]);

  useEffect(() => {
    const updatedCompletedImages = [...(completedImages || [])];
    updatedCompletedImages.forEach((image, index) => {
      if (image.file && image.gpsData === undefined) {
        EXIF.getData(image.file, function () {
          const gpsLat = EXIF.getTag(this, 'GPSLatitude');
          const gpsLon = EXIF.getTag(this, 'GPSLongitude');
          const gpsLatRef = EXIF.getTag(this, 'GPSLatitudeRef');
          const gpsLonRef = EXIF.getTag(this, 'GPSLongitudeRef');
          const timestamp = EXIF.getTag(this, 'DateTime');
          const capturedInLessThan2Minutes =
            image.file.lastModified &&
            Date.now() - image.file.lastModified < 2 * 60 * 1000;
          const updateImage = (newData) => {
            updatedCompletedImages[index] = {
              ...updatedCompletedImages[index],
              ...newData,
            };
            formContext.set('completedImages', [...updatedCompletedImages]);
          };
          if (gpsLat && gpsLon) {
            const lat = dmsToDecimal(gpsLat, gpsLatRef);
            const lon = dmsToDecimal(gpsLon, gpsLonRef);
            updateImage({
              gpsData: { lat, lon },
              gps_latitude: lat,
              gps_longitude: lon,
              timestamp: timestamp || null,
            });
          } else if (!gpsLat && !gpsLon && capturedInLessThan2Minutes) {
            getCurrentPosition().then(({ latitude, longitude }) => {
              updateImage({
                gpsData: { lat: latitude, lon: longitude },
                gps_latitude: latitude,
                gps_longitude: longitude,
                timestamp: getCurrentDateTime(),
              });
            });
          } else {
            updateImage({
              gpsData: null,
              gps_latitude: null,
              gps_longitude: null,
              timestamp: null,
            });
          }
        });
      }
    });
  }, [completedImages]);

  // Asset dropdown: only show when the inspection's form has asset tracking
  // enabled (same condition that gates the Assets tab in InspectionDetail).
  useEffect(() => {
    if (isGuest || !hasAssetTrackingFeature || !project?.id || !inspectionId) {
      setAssetTrackingEnabled(false);

      return;
    }
    (async () => {
      try {
        const targetTemplateId =
          templateID ||
          inspectionContext?.inspection?.template_id ||
          (await inspectionApi.get(inspectionId))?.inspection?.template_id;

        const forms = await schedulerApi.getProjectForms(project.id);
        const enabledForm = forms.data?.find(
          (form) =>
            form.template_id === targetTemplateId &&
            form.is_asset_tracking_enabled,
        );

        if (!enabledForm?.is_asset_tracking_enabled) {
          setAssetTrackingEnabled(false);

          return;
        }
        setAssetTrackingEnabled(true);
        const assets = (await projectAssetsApi.index(project.id, inspectionId)) || [];

        // Keep each asset's coordinates to pre-fill Location on selection.
        assetLocations.current = assets.reduce((locations, asset) => {
          if (asset.latitude != null && asset.longitude != null) {
            locations[asset.id] = `${asset.latitude}, ${asset.longitude}`;
          }

          return locations;
        }, {});

        setAssetOptions(
          assets.map((asset) => ({
            label: [asset.asset_type?.code, asset.asset_name]
              .filter(Boolean)
              .join(' — '),
            value: asset.id,
          })),
        );
      } catch (err) {
        setAssetTrackingEnabled(false);
      }
    })();
  }, [project?.id, inspectionId, templateID, hasAssetTrackingFeature, isGuest]);

  // Picking an asset pre-fills Location with its lat/long, matching the flow
  // where a finding is added from the asset card. The first run is skipped so
  // opening an existing finding keeps its saved location.
  useEffect(() => {
    if (!assetPrefillReady.current) {
      assetPrefillReady.current = true;

      return;
    }

    const assetLocation =
      assetLocations.current[formContext.value.project_asset_id];

    if (assetLocation) {
      formContext.set('location', assetLocation);
    }
  }, [formContext.value.project_asset_id]);

  const getLocation = async () => {
    try {
      const { latitude, longitude } = await getCurrentPosition();
      formContext.set('location', `${latitude}, ${longitude}`);
    } catch (err) {
    }
  };

  /**
   * SW2-2136 — rename a photo by clicking its name in the gallery.
   *
   * The pencil beside the name is CSS only (`_Findings.scss`, `.controls > span::after`), and
   * the gallery is lib-form's, so the click is caught here by delegation. The photo is found
   * by its displayed name: with two identically named photos, the first is renamed.
   */
  // Works on Add Finding too: a photo not yet uploaded has no guid, so handleRenameSave renames it
  // on the form only, and the upload sends that name (upload.js, `name || file.name`).
  const handleGalleryClick = (e) => {
    if (readOnly) return;
    if (e.target.tagName !== 'SPAN' || !e.target.closest('.controls')) return;

    const name = e.target.textContent.trim();

    for (const fieldKey of ['images', 'completedImages']) {
      const list = formContext.value[fieldKey] || [];
      const index = list.findIndex((img) => img.name === name);

      if (index !== -1) {
        setRenameTarget({ ...list[index], fieldKey, index });
        setRenameValue(name);

        return;
      }
    }
  };

  // The new name is what the printed Photo Log shows: PUT /uploads/:GUID/name sets uploads.name.
  const handleRenameSave = async () => {
    const newName = renameValue.trim();

    if (!renameTarget || !newName) return;

    const { fieldKey, index, guid } = renameTarget;

    // Saved first, and only then shown: uploadApi.rename catches and logs, resolving undefined on
    // failure, so updating the form first made a failed rename look saved until the next reload.
    // A photo with no guid is not on the server yet; its name goes up with the photo.
    if (guid) {
      const saved = await uploadApi.rename(guid, newName);

      if (!saved) {
        toast.error('The photo could not be renamed. Please try again.');

        return;
      }
    }

    const updated = [...(formContext.value[fieldKey] || [])];

    updated[index] = { ...updated[index], name: newName };
    formContext.set(fieldKey, updated);
    setRenameTarget(null);
  };

  // wait to gather roles and templates
  if (!appStore || loading) return <Loading />;

  /**
   * SW2-2230 — options for the Mapped Legend Item select.
   *
   * The field is *dependent on* Legend Item: it lists the numbers of that legend item's placements
   * on this inspection's site maps, and nothing else. Because the schema only spreads the field in
   * when `legendPlacementOptions.length` is non-zero, filtering here is also what implements
   * "hidden until a Legend Item is chosen" and "hidden when that item has no placements" — there is
   * no separate visibility flag to keep in sync.
   *
   * Options are bare numbers ("1", "2"), matching how the map labels them. The map name is appended
   * only to disambiguate the same number appearing on more than one site map.
   *
   * A finding can reference a placement that is no longer listed — removed from the map, or the
   * legend item since changed. Without a synthetic entry the select renders blank and reads as
   * unset, and the user cannot even clear it. `legend_positionable_label` comes from finding `show`.
   */
  const assignedPlacementId = formContext.value.legend_positionable_id;
  const selectedLegendItemId = Number(formContext.value.legend_item_id) || null;
  const placementsForLegendItem = selectedLegendItemId
    ? legendPlacements.filter(
        (placement) => Number(placement.legend_item_id) === selectedLegendItemId,
      )
    : [];
  const duplicatedNumbers = new Set(
    placementsForLegendItem
      .map((placement) => placement.number)
      .filter(
        (number, index, all) => all.indexOf(number) !== all.lastIndexOf(number),
      ),
  );
  const fetchedPlacementOptions = placementsForLegendItem.map((placement) => {
    const base = placement.number === null ? placement.label : `${placement.number}`;

    return {
      label:
        duplicatedNumbers.has(placement.number) && placement.map_name
          ? `${base} — ${placement.map_name}`
          : base,
      value: placement.id,
    };
  });
  const optionsWithAssigned =
    assignedPlacementId &&
    !fetchedPlacementOptions.some(
      (option) => option.value === Number(assignedPlacementId),
    )
      ? [
          {
            label:
              formContext.value.legend_positionable_label ||
              'Assigned map placement',
            value: Number(assignedPlacementId),
          },
          ...fetchedPlacementOptions,
        ]
      : fetchedPlacementOptions;

  /**
   * A guest gets no picker at all.
   *
   * The fetch effect already skips for guests, so `fetchedPlacementOptions` is empty — but the
   * synthetic entry above keys off the finding's *stored* placement, which a guest viewing an
   * assigned finding does have. That rendered a one-option dropdown reading "Assigned map
   * placement": a control offering a choice the guest cannot make, on a field they cannot edit.
   * Since option count is what makes the field render at all, emitting none removes it outright.
   *
   * The leading blank is what makes the field clearable. It is documented as optional, but the
   * only entries were the placements themselves, so choosing one was irreversible. The normalise
   * effect above already maps the select's `''` back to `null`.
   */
  const legendPlacementOptions =
    legendPlacementEnabled && !isGuest && optionsWithAssigned.length
      ? [{ label: '', value: '' }, ...optionsWithAssigned]
      : [];

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      {/* SW2-2136: the class draws the rename pencil beside each photo name (_Findings.scss), only
          where a click on the name opens Rename Photo. */}
      <div
        className={readOnly ? undefined : 'finding-photos--renamable'}
        onClick={handleGalleryClick}
      >
      <UploadControlContextProvider
        {...{
          allowDelete: readOnly ? false : permCheck('delete', 'Finding Images'),
          allowAnnotation: readOnly ? false : permCheck('all', 'Annotations'),
          allowCreate: readOnly ? false : true,
          allowRotate: readOnly ? false : true,
          allowGeolocation:
            tenantFeatureContext.tenantHasFeature(
              'Enable Photo Metadata Requirement',
            ) && project &&
            project.enable_photo_metadata_requirement &&
            enablePhotoMetadataClient,
          getMapUrl: showLocationOnMap,
          getUrl: uploadApi.getDownloadUrl,
        }}
      >
        <FormSchemaFields
          className='findings-form'
          schema={getSchema(
            templateID,
            commentOnly,
            appStore.get('user.roleName'),
            permCheck,
            {
              // SW2-2242. The inspection's template, matching the row the PDF pipeline gates on.
              name: gateTemplateName,
              condition_options: appStore.get(
                'constants.findingConditionOptions',
              ),
              findings_action: findingAction,
            },
            readOnly,
            disableFieldRelatedToDateCompleted,
            formContext.value,
            enableEditComments,
            legendItems,
            projectPermissionContext.readOnly,
            project ? project.start_date : null,
            conditionOptions,
            isPriorityEnabled,
            priorityOptions,
            disableImage,
            findingsMetricsTracking,
            inspectionId,
            modal,
            formContext,
            projectContext && typeof projectContext.isDownloadedByLoggedInUser === 'function' 
            ? !projectContext.isDownloadedByLoggedInUser()
            : false,
            getLocation,
            isGuest,
            isCompleted,
            fromFindingAdd,
            inspectionContext.isFindingBackdatingDisabled,
            showObservationDatesOfFindings,
            requireClosurePhotos,
            assetTrackingEnabled,
            assetOptions,
            fixedAssetId,
            legendPlacementEnabled,
            legendPlacementOptions,
          )}
          formData={formContext.value}
          onChange={formContext.set}
        />
      </UploadControlContextProvider>
      </div>
      <Modal
        show={!!renameTarget}
        title='Rename Photo'
        submitBtnText='Save'
        cancelBtnText='Cancel'
        handleClose={() => setRenameTarget(null)}
        handleSubmit={handleRenameSave}
      >
        <input
          className='form-control'
          aria-label='Photo name'
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleRenameSave()}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      </Modal>
      <Modal
        show={showMetadataWarning}
        title='Warning'
        subTitle={[`Please remove images that do not contain the required metadata to save changes to this finding.`]}
        cancelBtn={false}
        submitBtnText="Ok"
        handleClose={() => setShowMetadataWarning(false)}
        handleSubmit={() => setShowMetadataWarning(false)}
      >
        <div>
          <ol>
            {noMetadataImage.map((image, index) => (
              <li key={index}>{image.name}</li>
            ))}
          </ol>
        </div>
      </Modal>
    </>
  );
}

FindingsForm.propTypes = {
  commentOnly: PropTypes.bool, // only display "observations" and "comments"
  readOnly: PropTypes.bool,
  templateName: PropTypes.string.isRequired,
  /**
   * Used to set the default _initial_ observation.
   */
  defaultObservation: PropTypes.string,

  /**
   * Determine if the edit and delete actions are available for comments/notes
   *
   * When true, the schema function will check user permission/role,
   * before rendering the edit & delete controls.
   *
   * When false, the edit and delete controls will never be rendered
   */
  enableEditComments: PropTypes.bool,
  inspectionId: PropTypes.number.isRequired,
};

FindingsForm.defaultProps = {
  initialValue: {},
  commentOnly: false,
  readOnly: false,
  templateName: 'Unknown',
  defaultObservation: '',
  enableEditComments: true,
};

export default FindingsForm;
